import { AccountTree, ArrowBack, Construction, Inventory2, Shield, Sync, UploadFile, Warning } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { ChangeEvent, FC, Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import EpiStatCard from "../../components/epi/EpiStatCard";
import { EPI_COLORS } from "../../components/epi/epiColors";
import RiskSpinner from "../../components/risk-assessment/RiskSpinner";
import { LINK_SOURCE_COMPANY, countLabel } from "../../components/risk-assessment/riskEditorState";
import { readErrorMessage } from "../../components/risk-assessment/riskErrors";
import { RiskAssessmentService } from "../../services/RiskAssessmentService";
import { RiskPanelDTO, RiskPanelEventDTO } from "../../types/RiskAssessmentDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import './RiskAssessmentPanel.scss';

const RiskAssessmentPanel: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [panel, setPanel] = useState<RiskPanelDTO | null>(null);
	const [loading, setLoading] = useState(true);
	const [importing, setImporting] = useState(false);
	const [refreshing, setRefreshing] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const navigate = useNavigate();
	const { verifyPermission } = useUtils();
	const { showAlert, showError } = useAlert();
	const service = new RiskAssessmentService();

	useEffect(() => {
		loadPanel();
		if (!!permissions.length) {
			verifyPermission(permissions, 'RISK_ASSESSMENT');
		}
	}, []);

	return (
		<Fragment>
			<AppBar
				modulo="Avaliação de riscos"
			/>
			<div className="risk-assessment-panel">
				<div className="top">
					<Typography variant="h6">Painel da avaliação de riscos</Typography>
					<Button variant="outlined" startIcon={<ArrowBack/>} onClick={() => navigate('/avaliacao-riscos')}>
						Voltar ao histórico
					</Button>
				</div>
				{loading
					? getLoading()
					: !panel
						? <div className="sem-execucoes">
							<Typography variant="h6">Não foi possível carregar o painel. Recarregue a página para tentar novamente.</Typography>
						</div>
						: <div className="secoes">
							<section className="secao">
								<Typography variant="h6">Catálogo de EPIs</Typography>
								<Typography variant="body2" className="descricao">
									Importe a planilha de EPIs exportada do SGG, somente com as colunas Código, CA, Nome, Situação da
									Validade e Situação, nesta ordem. Somente os EPIs com Situação "Ativo" e Situação da Validade
									"Válido" são mantidos; o código e o CA alimentam a aba CA(s).
								</Typography>
								<div className="cards">
									<EpiStatCard
										icon={<Inventory2/>}
										color={EPI_COLORS.info}
										value={String(panel.catalogTotal)}
										label="EPIs ativos e válidos no catálogo"
										detail={eventDetail(panel.lastImport, 'Nenhuma importação realizada')}
									/>
								</div>
								<div className="botoes">
									<Button
										variant="contained"
										startIcon={importing ? <RiskSpinner size={18}/> : <UploadFile/>}
										disabled={importing}
										onClick={() => inputRef.current?.click()}
									>
										Importar planilha de EPIs
									</Button>
									<input ref={inputRef} hidden type="file" accept=".xlsx" onChange={importCatalog}/>
								</div>
							</section>
							<section className="secao">
								<Typography variant="h6">Vínculos de riscos</Typography>
								<Typography variant="body2" className="descricao">
									Os vínculos entre riscos, EPIs, EPCs e medidas de controle são lidos das avaliações da
									empresa {LINK_SOURCE_COMPANY}.
								</Typography>
								<div className="cards">
									<EpiStatCard icon={<Warning/>} color={EPI_COLORS.warning} value={String(panel.risks)}
										label="Riscos com vínculos" detail={eventDetail(panel.lastRefresh, 'Nenhuma atualização manual realizada')}/>
									<EpiStatCard icon={<Shield/>} color={EPI_COLORS.info} value={String(panel.epis)} label="EPIs vinculados"/>
									<EpiStatCard icon={<Construction/>} color={EPI_COLORS.purple} value={String(panel.epcs)} label="EPCs vinculados"/>
									<EpiStatCard icon={<AccountTree/>} color={EPI_COLORS.success} value={String(panel.measures)} label="Medidas de controle vinculadas"/>
								</div>
								<div className="botoes">
									<Button
										variant="contained"
										startIcon={refreshing ? <RiskSpinner size={18}/> : <Sync/>}
										disabled={refreshing}
										onClick={refreshLinks}
									>
										Atualizar vínculos de riscos
									</Button>
									<Button
										variant="outlined"
										startIcon={<AccountTree/>}
										onClick={() => window.open('/avaliacao-riscos/vinculos', '_blank')}
									>
										Ver árvore de vínculos
									</Button>
								</div>
							</section>
						</div>
				}
			</div>
		</Fragment>
	);

	async function loadPanel() {
		const response = await service.getPanel();
		if (!response) {
			showError('Não foi possível carregar o painel.');
		}
		setPanel(response?.data || null);
		setLoading(false);
	}

	async function importCatalog(event: ChangeEvent<HTMLInputElement>) {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file) {
			return;
		}
		setImporting(true);
		const form = new FormData();
		form.append('file', file);
		try {
			const response = await service.importEpiCatalog(form);
			const result = response.data;
			showAlert(`Catálogo importado: ${countLabel(result.inserted, 'inserido', 'inseridos')}, `
				+ `${countLabel(result.updated, 'atualizado', 'atualizados')}, ${result.unchanged} sem alteração, `
				+ `${countLabel(result.removed, 'removido', 'removidos')} e ${countLabel(result.ignored, 'ignorado', 'ignorados')}.`);
			await loadPanel();
		} catch (error) {
			showError(await readErrorMessage(error, 'Não foi possível importar a planilha de EPIs.'));
		}
		setImporting(false);
	}

	async function refreshLinks() {
		setRefreshing(true);
		try {
			const response = await service.refreshLinks();
			const result = response.data;
			showAlert(`Vínculos atualizados: ${countLabel(result.updated, 'avaliação consultada', 'avaliações consultadas')} no SGG`
				+ (result.removed
					? ` e ${countLabel(result.removed, 'avaliação excluída no SGG removida', 'avaliações excluídas no SGG removidas')}.`
					: '.'));
			await loadPanel();
		} catch (error) {
			showError(await readErrorMessage(error, 'Não foi possível atualizar os vínculos de riscos.'));
		}
		setRefreshing(false);
	}

	function eventDetail(event: RiskPanelEventDTO | undefined, empty: string) {
		return event ? `Última vez em ${event.createdAt} por ${event.name}` : empty;
	}
};

export default RiskAssessmentPanel;
